import { useState, useEffect } from "react";
import { ProSidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Box, Typography, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import GrassOutlinedIcon from '@mui/icons-material/GrassOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import Groups3OutlinedIcon from '@mui/icons-material/Groups3Outlined';
import CallMissedOutgoingIcon from '@mui/icons-material/CallMissedOutgoing';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';

import {
  ConfirmationNumberOutlined,
  ViewModuleOutlined,
  AssignmentOutlined,
  AgricultureOutlined
} from "@mui/icons-material";
import { useSelector } from "react-redux";

const Item = ({ title, to, icon, selected, setSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <MenuItem
      active={selected === title}
      style={{
        marginTop: "6px",
        color: colors.grey[100],
      }}
      onClick={() => setSelected(title)}
      icon={icon}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

const Sidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selected, setSelected] = useState("");
  const location = useLocation();

  // Get user from Redux state
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    // Set the selected state based on the current path
    const path = location.pathname;

    if (path === "/") {
      setSelected("Mi empresa");
    } else if (path.startsWith("/alerts")) {
      setSelected("Alertas");
    } else if (path.startsWith("/templates")) {
      setSelected("Plan de aplicaciones");
    } else if (path.startsWith("/lotes")) {
      setSelected("Lotes");
    } else if (path.startsWith("/tickets")) {
      setSelected("Tickets");
    } else if (path.startsWith("/products")) {
      setSelected("Productos");
    } else if (path.startsWith("/applications")) {
      setSelected("Aplicaciones");
    } else if (path.startsWith("/forces")) {
      setSelected("Forzas");
    } else if (path.startsWith("/suppliers")) {
      setSelected("Proveedores");
    } else if (path.startsWith("/purchases")) {
      setSelected("Compras");
    } else if (path.startsWith("/usages")) {
      setSelected("Consumo/Salidas");
    } else if (path.startsWith("/counting")) {
      setSelected("Recuento");
    }

  }, [location]);

  return (
    <Box
      sx={{
        "& .pro-sidebar": {
          width: "210px",
          minWidth: "210px"
        },
        "& .pro-sidebar-inner": {
          background: `${colors.primary[400]} !important`,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 10px 5px 20px !important",
        },
        "& .pro-inner-item:hover": {
          color: `${colors.greenAccent[400]} !important`
        },
        "& .pro-menu-item.active": {
          color: `${colors.greenAccent[400]} !important`,
        },
      }}
    >
      <ProSidebar>
        <Menu iconShape="square">
          <Box mb="25px">
            <Box display="flex" justifyContent="center" alignItems="center">
              <img
                alt="company-image"
                src={user?.company_logo}
                style={{
                  width: "100%", 
                  maxWidth: "150px",
                  height: "18vh",
                  borderRadius: "8px",
                  objectFit: "cover"
                }}
              />
            </Box>
            <Box textAlign="center">
              <Typography
                variant="h3"
                color={colors.grey[100]}
                fontWeight="bold"
                sx={{ m: "10px 0 0 0" }}
              >
                {user?.name}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              height: "70vh", // Adjust the height as needed
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "5px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: colors.greenAccent[600],
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: colors.primary[400],
              },
            }}
          >

            {/* General Section with Dropdown */}
            <SubMenu
              title="General"
              defaultOpen={true}
            >
              <Item
                title="Mi empresa"
                to="/"
                icon={<HomeOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
              />
              <Item
                title="Equipo"
                to="/team"
                icon={<Groups3OutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
              />
              {/* <Item
                title="Alertas"
                to="/alerts"
                icon={<WarningAmberOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
              /> */}
              <Item
                title="Exportar"
                to="/export"
                icon={<FileUploadOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
              />
            </SubMenu>

            {/* Aplicativo Section with Dropdown */}
            <SubMenu
              title="Aplicativo"
              defaultOpen={true}
            >
              <Item
                title="Plan de aplicaciones"
                to="/templates"
                icon={<AssignmentOutlined />}
                selected={selected}
                setSelected={setSelected}
              />
              <Item
                title="Tickets"
                to="/tickets"
                icon={<ConfirmationNumberOutlined />}
                selected={selected}
                setSelected={setSelected}
              />
              <Item
                title="Lotes"
                to="/lotes"
                icon={<ViewModuleOutlined />}
                selected={selected}
                setSelected={setSelected}
              />

              <Item
                title="Forzas"
                to="/forces"
                icon={<GrassOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
              />
              
              <Item
                title="Aplicaciones"
                to="/applications"
                icon={<AgricultureOutlined />}
                selected={selected}
                setSelected={setSelected}
              />
            </SubMenu>

            {/* Inventory Section with Dropdown */}
            <SubMenu
              title="Inventario"
              defaultOpen={true}
            >
              <Item
                title="Compras"
                to="/purchases"
                icon={<ReceiptLongOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
              />

              <Item
                title="Proveedores"
                to="/suppliers"
                icon={<LocalShippingOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
              />

              <Item
                title="Productos"
                to="/products"
                icon={<CategoryOutlinedIcon />}
                selected={selected}
                setSelected={setSelected}
              />

              <Item
                title="Consumo/Salidas"
                to="/usages"
                icon={<CallMissedOutgoingIcon />}
                selected={selected}
                setSelected={setSelected}
              />

              {/* <Item
                title="Recuento"
                to="/countig"
                icon={<FormatListNumberedIcon />}
                selected={selected}
                setSelected={setSelected}
              /> */}
            </SubMenu>

          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;